import {
  BufferTrackerReact as tracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'
import {
  PUBLIC_TIER_ATTRIBUTION_COOKIE,
  PUBLIC_TIER_ATTRIBUTION_TTL,
  PUBLIC_TIER_CONSENT_VERSION_COOKIE,
  readPublicTierAttribution,
} from '@buffer-mono/shared-utils/publicTierAttribution'
import type { PublicTierAttribution } from '@buffer-mono/shared-utils/publicTierAttribution'

import { env } from '~publish/env'
import { isAnalyticsConsented } from '../publicCookieConsent'
import { getCurrentPublicPageBotSignal } from '../publicPageBotGuard'
import { isPublicPublishPath } from '../publicPublishPaths'
import { createPublicSessionStore, PUBLIC_VISITOR_STORAGE_KEY } from './session'
import { PublicEntryType, PublicHandoffSource } from './types'
import { clearAllHandoffTracking } from './handoff'
import type {
  PublicDraftCreationSource,
  PublicImageUploadAction,
  PublicShareMethod,
  PublicSignupTrigger,
  PublicSurface,
  PublicSurfaceState,
} from './types'

export * from './types'

const store = createPublicSessionStore({
  storage: {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
    removeItem: (key) => window.sessionStorage.removeItem(key),
  },
  now: Date.now,
  onSave: refreshConsentVersion,
  generateId: () => crypto.randomUUID(),
  getConsentVersion: () =>
    readBrowserCookies()[PUBLIC_TIER_CONSENT_VERSION_COOKIE] ?? '',
})
const base = { product: Product.Publish, clientName: Client.PublishWeb }
let currentSurface:
  | { surface: PublicSurface; surfaceState: PublicSurfaceState }
  | undefined

function cookieScope(): string {
  const suffix = window.location.hostname.split('publish.')[1]
  return `Path=/; SameSite=Lax; Secure${suffix ? `; Domain=${suffix}` : ''}`
}

function refreshConsentVersion(): void {
  try {
    const version =
      readBrowserCookies()[PUBLIC_TIER_CONSENT_VERSION_COOKIE] ??
      crypto.randomUUID()
    document.cookie = `${PUBLIC_TIER_CONSENT_VERSION_COOKIE}=${encodeURIComponent(version)}; Max-Age=${PUBLIC_TIER_ATTRIBUTION_TTL / 1000}; ${cookieScope()}`
  } catch {
    /* Tracking can continue when cookies are blocked. */
  }
}

export function clearPublicTracking(): void {
  store.clear()
  clearAllHandoffTracking()
  document.cookie = `${PUBLIC_TIER_ATTRIBUTION_COOKIE}=; Max-Age=0; ${cookieScope()}`
}

export function withdrawPublicTracking(): void {
  document.cookie = `${PUBLIC_TIER_CONSENT_VERSION_COOKIE}=${crypto.randomUUID()}; Max-Age=${PUBLIC_TIER_ATTRIBUTION_TTL / 1000}; ${cookieScope()}`
  clearPublicTracking()
}

function permitted(): boolean {
  if (
    !isPublicPublishPath(window.location.pathname) ||
    !isAnalyticsConsented() ||
    !env.VITE_PUBLIC_SEGMENT_KEY ||
    Reflect.get(document, 'prerendering') === true ||
    getCurrentPublicPageBotSignal()
  )
    return false
  const consent = readBrowserCookies()['cookieyes-consent']
  if (consent?.split(',').some((entry) => entry.trim() === 'analytics:no')) {
    clearPublicTracking()
    return false
  }
  return true
}

function emit(send: () => void): void {
  try {
    send()
  } catch {
    /* Analytics must not interrupt guest product use. */
  }
}

function context():
  | {
      product: Product
      clientName: Client
      isPublicTier: boolean
      publicSessionId: string
    }
  | undefined {
  if (!permitted() || !currentSurface) return undefined
  const surface = currentSurface
  let session = store.read()
  if (!session) {
    const params = new URLSearchParams(window.location.search)
    let referrer: URL | undefined
    try {
      referrer = document.referrer ? new URL(document.referrer) : undefined
    } catch {
      /* Invalid referrers have no attribution. */
    }
    const isMarketingReferrer =
      referrer?.hostname === 'buffer.com' ||
      referrer?.hostname === 'www.buffer.com'
    const entrySource = (
      params.get('entrySource') ??
      params.get('source') ??
      (isMarketingReferrer
        ? referrer?.pathname.replace(/^\/|\/$/g, '') || 'home'
        : referrer?.hostname) ??
      'direct'
    ).slice(0, 200)
    let isReturningVisitor = false
    try {
      isReturningVisitor =
        localStorage.getItem(PUBLIC_VISITOR_STORAGE_KEY) === '1'
      localStorage.setItem(PUBLIC_VISITOR_STORAGE_KEY, '1')
    } catch {
      /* Tracking can continue without persistent storage. */
    }
    refreshConsentVersion()
    session = store.start(entrySource)
    const entryType =
      window.self !== window.top
        ? PublicEntryType.EMBEDDED_DEMO
        : isMarketingReferrer
          ? PublicEntryType.MARKETING_REDIRECT
          : referrer
            ? PublicEntryType.OTHER
            : PublicEntryType.DIRECT
    const enteredSession = session
    emit(() =>
      tracker.publicTierEntered({
        ...base,
        isPublicTier: true,
        publicSessionId: enteredSession.id,
        entrySource,
        entryType,
        entrySurface: surface.surface,
        entryUrl: window.location.href,
        isReturningVisitor,
        utmSource: params.get('utm_source'),
        utmMedium: params.get('utm_medium'),
        utmCampaign: params.get('utm_campaign'),
        utmContent: params.get('utm_content'),
      }),
    )
  }
  const common = { ...base, isPublicTier: true, publicSessionId: session.id }
  if (!session.surfaces.includes(currentSurface.surface)) {
    const first = session.surfaces.length === 0
    session.surfaces.push(currentSurface.surface)
    store.save(session)
    emit(() =>
      tracker.publicTierSurfaceViewed({
        ...common,
        ...surface,
        isFirstSurfaceInSession: first,
      }),
    )
  }
  return common
}

export function trackPublicSurface(
  surface: PublicSurface,
  surfaceState: PublicSurfaceState,
): void {
  currentSurface = { surface, surfaceState }
  context()
}

function actionContext(): ReturnType<typeof context> {
  const common = context()
  const session = common && store.read()
  if (session)
    store.save({
      ...session,
      actionCount: session.actionCount + 1,
      lastActionAt: Date.now(),
    })
  return common
}

export function preparePublicSignup(signupTrigger: PublicSignupTrigger): void {
  const common = context()
  const session = common && store.read()
  if (!session) {
    clearPublicTracking()
    return
  }
  const attribution: PublicTierAttribution = {
    isPublicTier: true,
    publicSessionId: session.id,
    entrySource: session.entrySource,
    firstEnteredPublicTierAt: new Date(session.startedAt).toISOString(),
    publicActionCount: session.actionCount,
    signupTrigger,
  }
  document.cookie = `${PUBLIC_TIER_ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(attribution))}; Max-Age=${PUBLIC_TIER_ATTRIBUTION_TTL / 1000}; ${cookieScope()}`
}

function readBrowserCookies(): Record<string, string> {
  const cookies: Record<string, string> = {}
  for (const cookie of document.cookie.split(';')) {
    const [key, ...value] = cookie.trim().split('=')
    if (!key) continue
    try {
      cookies[key] = decodeURIComponent(value.join('='))
    } catch {
      continue
    }
  }
  return cookies
}

export function getPublicSignupAttribution():
  | PublicTierAttribution
  | undefined {
  return readPublicTierAttribution(readBrowserCookies())
}

export function trackPublicCta(
  cta: string,
  signupTrigger?: PublicSignupTrigger,
): void {
  const common = actionContext()
  if (!common) {
    if (signupTrigger) clearPublicTracking()
    return
  }
  emit(() => tracker.cTAClicked({ ...common, cta, upgradePathName: null }))
  if (signupTrigger) preparePublicSignup(signupTrigger)
}

export function trackPublicCtaViewed(cta: string): boolean {
  const common = context()
  if (!common) return false
  emit(() =>
    tracker.cTAViewed({
      ...common,
      organizationId: null,
      cta,
      upgradePathName: null,
    }),
  )
  return true
}

export function trackPublicDraftCreated(properties: {
  creationSource: PublicDraftCreationSource
  templateId?: string
  hasMedia: boolean
  draftCount: number
}): void {
  const common = actionContext()
  if (common)
    emit(() =>
      tracker.publicTierDraftCreated({
        ...common,
        ...properties,
        storageLocation: 'browser',
      }),
    )
}

export function trackPublicShareOpened(text: string, hasMedia: boolean): void {
  const common = actionContext()
  if (common)
    emit(() =>
      tracker.shareNowOpened({
        ...common,
        hasText: text.trim().length > 0,
        textLength: text.length,
        hasMedia,
      }),
    )
}

export function trackPublicShareCompleted(
  shareMethod: PublicShareMethod,
  hasMedia: boolean,
  channel?: string,
): void {
  const common = actionContext()
  if (common)
    emit(() =>
      tracker.shareNowCompleted({ ...common, shareMethod, hasMedia, channel }),
    )
}

export function trackPublicDraftHandoff(
  publicSessionId: string,
  properties: {
    organizationId: string
    hasText: boolean
    hasMedia: boolean
    promotionSucceeded: boolean
    draftCount: number
    draftsImportedCount: number
    mediaExpiredCount: number
  },
): void {
  const attribution = getPublicSignupAttribution()
  if (!attribution || attribution.publicSessionId !== publicSessionId) return
  const { isPublicTier } = attribution
  emit(() =>
    tracker.composerWrapperDraftLoaded({
      ...base,
      isPublicTier,
      publicSessionId,
      ...properties,
      handoffSource: PublicHandoffSource.PUBLIC_TIER,
    }),
  )
}

export function getPublicActionSessionId(): string | undefined {
  return context()?.publicSessionId
}

export function isCurrentPublicAction(sessionId: string | undefined): boolean {
  return !!sessionId && permitted() && store.read()?.id === sessionId
}

export function trackPublicImageUploaded(
  sessionId: string | undefined,
  uploadAction: PublicImageUploadAction,
  imageCount: number,
): void {
  if (!isCurrentPublicAction(sessionId)) return
  const common = actionContext()
  if (common)
    emit(() =>
      tracker.publicTierImageUploaded({ ...common, uploadAction, imageCount }),
    )
}

export function trackPublicImageEdited(sessionId: string | undefined): void {
  if (!isCurrentPublicAction(sessionId)) return
  const common = actionContext()
  if (common)
    emit(() => tracker.publicTierImageEdited({ ...common, imageCount: 1 }))
}
